/**
 * `<AnalysisGenerationBar>` — JD selector + ghost Replace Transcript (B-13).
 *
 * Matches prototype L1573-1583 (`.gen-bar`).
 *
 * Placed at TOP of the `analysis-loaded` state.
 *
 * Prototype CSS (L300):
 *   .gen-bar { display:flex; gap:8px; align-items:center; margin-bottom:14px; flex-wrap:wrap; }
 *   .gen-bar select { flex:1; min-width:140px; bg:var(--card); border:1px solid var(--bdr2);
 *                     padding:6px 8px; border-radius:5px; font-size:12px; font-family:inherit;
 *                     cursor:pointer; }
 *   .gen-bar select:focus { outline:none; border-color:var(--teal); }
 *
 * JD select: tab-local generation context (Q2 ruling). Accepts `jdId`, `jds`,
 * `onJdChange` (page-level select = viewing context; this = generation context).
 *
 * Replace Transcript: ghost `.btn-g`, only when transcript exists (prototype L1581).
 *
 * B-13.3.1 layout: buttons right-aligned per AI Evaluation + Interview Prep canon
 * pattern (`justify-between` + `ml-auto` buttons group). JD select stays left;
 * Replace Transcript sits in a right-aligned group.
 *
 * Regenerate Analysis removed (auto-eval-prep): the analysis artifact is
 * deterministic. `hasAnalysis` prop retained for callers but the CTA is
 * no longer rendered when it is true.
 */
import type { JSX } from "react";

interface JdSummary {
  id: string;
  name: string;
}

interface AnalysisGenerationBarProps {
  /**
   * Whether an analysis already exists.
   * When true, the Generate CTA is hidden (artifact is deterministic —
   * no re-run affordance; auto-eval-prep change).
   */
  hasAnalysis: boolean;
  /** Currently selected JD id for this generation context. */
  jdId?: string | undefined;
  /** Available JDs to pick from. */
  jds?: JdSummary[];
  /** Called when JD select changes. */
  onJdChange?: (jdId: string | undefined) => void;
  /** Called when the primary CTA is clicked (first-time generation only). */
  onGenerate: () => void;
  /** Whether transcript is loaded (shows Replace Transcript ghost button). */
  hasTranscript?: boolean;
  /** Called when Replace Transcript ghost button is clicked. */
  onReplaceTranscript?: () => void;
  /** Whether a mutation is in flight (disable + show pending state). */
  isPending?: boolean;
}

export function AnalysisGenerationBar({
  hasAnalysis,
  jdId,
  jds = [],
  onJdChange,
  onGenerate,
  hasTranscript = false,
  onReplaceTranscript,
  isPending = false,
}: AnalysisGenerationBarProps): JSX.Element {
  return (
    <div
      className="mb-3.5 flex flex-wrap items-center justify-between gap-2"
      data-testid="analysis-generation-bar"
    >
      {/* JD select — tab-local generation context (Q2 ruling). Left side. */}
      {jds.length > 0 ? (
        <select
          value={jdId ?? ""}
          onChange={(e) => onJdChange?.(e.target.value || undefined)}
          data-testid="gen-bar-jd-select"
          className="min-w-[140px] flex-1 cursor-pointer rounded-[5px] border bg-surface-card px-2 py-1.5 font-sans text-[12px] text-text-primary outline-none focus:border-brand-teal"
          style={{
            borderColor: "var(--p-white-a13)", // --bdr2 in prototype; theme-invariant white hairline
          }}
        >
          <option value="">No JD</option>
          {jds.map((jd) => (
            <option key={jd.id} value={jd.id}>
              {jd.name}
            </option>
          ))}
        </select>
      ) : null}

      {/*
       * Action buttons — right-aligned (B-13.3.1).
       * Matches AI Evaluation top-bar + Interview Prep PrepActiveStageHeader pattern:
       * `<div className="flex items-center gap-2">` pushed right via `ml-auto`.
       */}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {/* Replace Transcript ghost — only when transcript exists (prototype L1581). */}
        {hasTranscript && onReplaceTranscript ? (
          <button
            type="button"
            onClick={onReplaceTranscript}
            disabled={isPending}
            data-testid="gen-bar-replace-transcript-btn"
            className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-[5px] border border-border-default bg-transparent font-medium text-text-secondary transition-colors hover:border-border-strong hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-60"
            style={{ fontSize: "12px", padding: "5px 12px" }}
          >
            Replace Transcript
          </button>
        ) : null}

        {/* Primary CTA — gradient .btn-p, small override per prototype L1579.
            Hidden when analysis already exists: the artifact is deterministic
            and no re-run affordance is surfaced (auto-eval-prep change). */}
        {!hasAnalysis ? (
          <button
            type="button"
            onClick={onGenerate}
            disabled={isPending}
            data-testid="gen-bar-generate-btn"
            className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-[5px] bg-brand-grad font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            style={{ fontSize: "12px", padding: "5px 14px" }}
          >
            Generate Analysis
          </button>
        ) : null}
      </div>
    </div>
  );
}
